import { RightArrowIcon } from '@jouzen/ecom-components/icons/RightArrowIcon';
import { Button, type ButtonProps } from '@jouzen/ecom-components/v3/Button';
import {
  GridItem,
  type GridItemProps,
} from '@jouzen/ecom-components/v3/GridItem';
import { IconTag } from '@jouzen/ecom-components/v3/IconTag';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';

import type { IconTagProps } from '../LandingPageHero/LandingPageIconTag';

const tValues = {
  em: function em(chunks: ReactNode) {
    return <em>{chunks}</em>;
  },
};

export interface LandingPageBlurbProps {
  readonly eyebrow?: MessageKey;
  readonly title: {
    key: MessageKey;
    colEnd?: GridItemProps['colEnd'];
    multiline: boolean;
    indentation?: string;
  };
  readonly description: {
    key: MessageKey;
    className?: string;
    colStart?: GridItemProps['colStart'];
    alignment: 'top' | 'center' | 'bottom';
  };
  readonly action?: Omit<ButtonProps, 'key' | 'children'> & {
    title: MessageKey;
    dataCy?: string;
    arrow?: boolean;
  };
  readonly iconTag?: IconTagProps;
}

const LandingPageBlurb = (props: LandingPageBlurbProps) => {
  const { eyebrow, title, description, action, iconTag } = props;

  const {
    title: actionTitle,
    dataCy: actionDataCy,
    arrow: actionArrow,
    ...actionButtonProps
  } = action ?? {};

  const mergedTitleColEnd: GridItemProps['colEnd'] = {
    sm: 'main',
    md: 'main',
    lg: 11,
    ...title.colEnd,
  };

  const mergedDescriptionColStart: GridItemProps['colStart'] = {
    sm: 'main',
    md: 'main',
    lg: 14,
    ...description.colStart,
  };

  const t = useTranslations();

  return (
    <div data-cy="landing-page-blurb">
      <LayoutGrid>
        {iconTag && (
          <GridItem
            className="z-10"
            colEnd={mergedTitleColEnd}
            colStart={{ sm: 'main', md: 'main', lg: 'main' }}
          >
            <IconTag
              icon={iconTag.icon}
              iconTagText={t(iconTag.iconTagText)}
              variant="transparent-light"
            />
          </GridItem>
        )}
        {eyebrow && (
          <GridItem
            className="z-10"
            colEnd={mergedTitleColEnd}
            colStart={{ sm: 'main', md: 'main', lg: 'main' }}
          >
            <div data-cy="landing-page-blurb-eyebrow">{t(eyebrow)}</div>
          </GridItem>
        )}
        <GridItem
          className="z-10 lg:pt-0"
          colEnd={mergedTitleColEnd}
          colStart={{ sm: 'main', md: 'main', lg: 'main' }}
        >
          <SectionHeading
            animated
            content={title.key}
            emAnimationProps={{
              transition: { duration: 2, type: 'tween', delay: 0.5 },
            }}
            indentation={title.indentation ?? 'sm:pl-20 md:pl-28'}
            multiline={title.multiline}
            typographyProps={{
              className: 'leading-heading tracking-tighter',
              color: 'inherit',
              Element: 'h3',
              variant: 'h2',
              style: {
                lineHeight: '1.1',
              },
            }}
            wrapperAnimationProps={{
              transition: { duration: 1.25, type: 'tween', delay: 0.25 },
            }}
          />
        </GridItem>
        <GridItem
          className={'z-10 py-4 lg:pt-2 lg:pb-5'}
          colEnd={{ sm: 'main' }}
          colStart={mergedDescriptionColStart}
        >
          <div
            className={cx('flex flex-col', {
              'justify-start': description.alignment === 'top',
              'justify-end': description.alignment === 'bottom',
              'justify-center': description.alignment === 'center',
            })}
            data-cy="landing-page-blurb-description-container"
          >
            <Typography
              className={cx('text-wrap', description.className)}
              color="inherit"
              data-cy="landing-page-blurb-description"
            >
              <RichText messageKey={description.key} tValues={tValues} />
            </Typography>
            {action && (
              <div data-cy="landing-page-blurb-action" className="z-10 mt-6">
                <Button
                  variant={actionArrow ? 'body-link' : 'secondary-dark'}
                  data-cy={actionDataCy}
                  {...actionButtonProps}
                >
                  <span
                    className={cx({
                      'flex flex-row items-center gap-2 underline': actionArrow,
                    })}
                  >
                    {t(actionTitle!)}
                    {actionArrow && <RightArrowIcon width="18" />}
                  </span>
                </Button>
              </div>
            )}
          </div>
        </GridItem>
      </LayoutGrid>
    </div>
  );
};

export default LandingPageBlurb;
